import { useState } from 'react'
import { Link, useParams } from 'react-router'
import {
  FormattedDate,
  FormattedMessage,
  FormattedTime,
  useIntl
} from 'react-intl'
import { useFetchCb } from '@gluedigital/ruse-fetch-extras'
import type { Company, CompanyDataResponse, User } from 'src/types/global'
import HeroBanner from '@components/HeroBanner/HeroBanner'
import { useCompanyData } from '@hooks/useCompanyData'
import { useToast } from '@hooks/useToast'
import BackButton from '@components/BackButton/BackButton'
import CompanyForm from '@components/CompanyForm/CompanyForm'
import UserForm from '@components/UserForm/UserForm'
import DeleteUserDialog from '@components/DeleteUserDialog/DeleteUserDialog'
import { ADMIN_URL } from 'src/constants/global'

type UserFormTarget = 'closed' | 'new' | User

export default function CompanyData() {
  const intl = useIntl()
  const { id } = useParams()
  const refresh = useFetchCb()
  const { showToast, toastElement } = useToast()
  const [isEditing, setIsEditing] = useState(false)
  const [userFormTarget, setUserFormTarget] = useState<UserFormTarget>('closed')
  const [userToDelete, setUserToDelete] = useState<User | null>(null)

  const data: CompanyDataResponse = useCompanyData(id)
  if (!data || data.company.id !== id) {
    return (
      <div className="container">
        <h1>
          <FormattedMessage id="companies.empty" />
        </h1>
      </div>
    )
  }

  const { company, users, datasets } = data

  const handleSaved = (updated: Company) => {
    refresh(`${ADMIN_URL}/companies/${updated.id}`)
    refresh(`${ADMIN_URL}/companies`)
    showToast(intl.formatMessage({ id: 'companies.updated' }))
  }

  const isNewUser = userFormTarget === 'new'
  const handleUserSaved = () => {
    refresh(`${ADMIN_URL}/companies/${id}`)
    showToast(
      intl.formatMessage({ id: isNewUser ? 'users.created' : 'users.updated' })
    )
  }

  const handleUserDeleted = () => {
    refresh(`${ADMIN_URL}/companies/${id}`)
    showToast(intl.formatMessage({ id: 'users.deleted' }))
  }

  return (
    <>
      <HeroBanner nameId="company">
        <>
          <div className="title">
            <BackButton />
            <h1 className="company-name">
              <span className="icon icon-home-stroke" />
              <span>{company.name}</span>
            </h1>
            <button type="button" onClick={() => setIsEditing(true)}>
              <span className="icon icon-pen" />
              <span>
                <FormattedMessage id="edit" />
              </span>
            </button>
          </div>
          <div className="company-details">
            <div>
              <strong>NIF:</strong> {company.nif}
            </div>
            <div className="company-date">
              <span className="icon icon-calendar" />
              <strong>
                <FormattedDate
                  value={company.created}
                  day="2-digit"
                  month="2-digit"
                  year="2-digit"
                />
              </strong>
            </div>
          </div>
        </>
      </HeroBanner>

      <section className="container" id="company-data">
        <article className="users-section">
          <div className="section-header">
            <h2>
              <span className="icon icon-profile" />
              <span>
                <FormattedMessage id="company-data.user.title" />
              </span>
            </h2>
            <button
              type="button"
              className="highlight-button"
              onClick={() => setUserFormTarget('new')}
            >
              <span className="icon icon-plus" />
              <span>
                <FormattedMessage id="users.new" />
              </span>
            </button>
          </div>
          {users.length === 0 ? (
            <p>
              <FormattedMessage id="dataset.table-data.empty" />
            </p>
          ) : (
            <ul id="user-grid" className="grid-wrapper">
              {[
                ...users.filter((u) => u.acl?.includes('org:owner')),
                ...users.filter((u) => !u.acl?.includes('org:owner'))
              ].map((user) => {
                const isAdmin = user.acl?.includes('org:owner')
                return (
                  <li
                    key={user.id}
                    className={`user-card ${isAdmin ? 'admin-card' : ''}`}
                  >
                    <h3>
                      <strong>{user.name}</strong>
                      {isAdmin && (
                        <span className={`badge role-admin`}>Responsable</span>
                      )}
                    </h3>
                    <small>({user.email})</small>
                    <p>
                      Alta:{' '}
                      <FormattedDate
                        value={user.created}
                        day="numeric"
                        month="short"
                        year="numeric"
                      />
                    </p>
                    <div className="user-card-actions">
                      <button
                        type="button"
                        onClick={() => setUserFormTarget(user)}
                      >
                        <span className="icon icon-pen" />
                        <span>
                          <FormattedMessage id="edit" />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setUserToDelete(user)}
                      >
                        <span className="icon icon-cross" />
                        <span>
                          <FormattedMessage id="delete" />
                        </span>
                      </button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </article>
        <article className="datasets-section">
          <h2>
            <span className="icon icon-pile-filled" />
            <span>
              <FormattedMessage id="company-data.datasets.title" />
            </span>
          </h2>
          {datasets.length === 0 ? (
            <p>
              <FormattedMessage id="dataset.table-data.empty" />
            </p>
          ) : (
            <ul id="dataset-grid" className="grid-wrapper">
              {datasets.map((dataset) => {
                const vid = dataset.connectorId
                  ? `${dataset.connector}:${dataset.connectorId}`
                  : dataset.id
                // const privacy =
                //   dataset.schema === 'public_data' ? 'public' : 'private'
                return (
                  <li key={dataset.id} className="dataset-card">
                    <h3>
                      <Link to={`/company/${id}/dataset/${dataset.id}`}>
                        <span>{dataset.title}</span>
                      </Link>
                    </h3>
                    <div className="table-data">
                      <span>
                        Id: <code title={vid}>{vid}</code>
                      </span>
                      <Link to={`/company/${id}/dataset/${dataset.id}`}>
                        <span>Ver datos</span>
                      </Link>
                      {/* <span className={`badge privacy-${privacy}`}>
                        <FormattedMessage
                          id={`company-data.datasets.privacy.${privacy}`}
                        />
                      </span> */}
                    </div>
                    <p className="updated-date">
                      Actualizado: <span className="icon icon-calendar" />
                      {dataset.lastUpdate && (
                        <>
                          <strong>
                            <FormattedDate
                              value={dataset.lastUpdate}
                              day="2-digit"
                              month="2-digit"
                              year="2-digit"
                            />
                          </strong>
                          <span className="icon icon-clock" />
                          <strong>
                            <FormattedTime value={dataset.lastUpdate} />
                          </strong>
                        </>
                      )}
                      {!dataset.lastUpdate && <span>Sin datos</span>}
                    </p>
                  </li>
                )
              })}
            </ul>
          )}
        </article>
      </section>

      {isEditing && (
        <CompanyForm
          company={company}
          onClose={() => setIsEditing(false)}
          onSaved={handleSaved}
        />
      )}

      {userFormTarget !== 'closed' && (
        <UserForm
          user={userFormTarget === 'new' ? undefined : userFormTarget}
          companyId={company.id}
          companyName={company.name}
          onClose={() => setUserFormTarget('closed')}
          onSaved={handleUserSaved}
        />
      )}

      {userToDelete && (
        <DeleteUserDialog
          user={userToDelete}
          onClose={() => setUserToDelete(null)}
          onDeleted={handleUserDeleted}
        />
      )}
      {toastElement}
    </>
  )
}
